import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC, Key } from 'react';
import { Input, Select, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ipdSkuApi } from '../api/services';
import type {
  IpdSkuListItemVO,
  IpdSkuSourceProjectOptionVO,
  IpdSkuStatusOptionVO,
} from '../api/types';
import {
  IpcButton,
  IpcFilterToolbar,
  IpcModal,
  IpcTable,
  IpcTag,
  IPC_FILTER_MULTI_SELECT_PROPS,
} from './ui';

const PAGE_SIZE = 20;

export function formatIpdSkuLabel(row: Pick<IpdSkuListItemVO, 'spec' | 'sku_code' | 'name'>): string {
  const spec = row.spec?.trim();
  if (spec) return spec;
  const code = row.sku_code?.trim();
  if (code) return code;
  return row.name?.trim() || '-';
}

export function formatIpdSkuSource(row: Pick<IpdSkuListItemVO, 'source_project_code' | 'source_project_name'>): string {
  const code = row.source_project_code?.trim() || '-';
  const name = row.source_project_name?.trim();
  return name ? `${code} · ${name}` : code;
}

export type IpdSkuOccupancy = 'available' | 'current' | 'other';

type Props = {
  open: boolean;
  ipcProjectId: number;
  scheduleId?: number;
  currentSpecId?: number;
  /** 标题下方的补充说明（如复制规格时的提示） */
  currentRowLabel?: string;
  /** 覆盖默认「添加规格」标题 */
  titlePrefix?: string;
  /** 只读查看：不可改选、无确认保存 */
  readOnly?: boolean;
  selected: IpdSkuListItemVO[];
  /** 返回每个 SKU 的占用状态（当前行 / 其他行 / 可选） */
  getOccupancy?: (row: IpdSkuListItemVO) => IpdSkuOccupancy;
  onCancel: () => void;
  onOk: (rows: IpdSkuListItemVO[]) => void | Promise<void>;
};

export const IpdSkuPickerModal: FC<Props> = ({
  open,
  ipcProjectId,
  scheduleId,
  currentSpecId,
  currentRowLabel,
  titlePrefix = '添加规格',
  readOnly = false,
  selected,
  getOccupancy,
  onCancel,
  onOk,
}) => {
  const [keywordDraft, setKeywordDraft] = useState('');
  const [sourceDraft, setSourceDraft] = useState<number | undefined>(undefined);
  const [statusDraft, setStatusDraft] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [sourceProjectWorkItemId, setSourceProjectWorkItemId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<string[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<IpdSkuListItemVO[]>([]);
  const [sourceProjects, setSourceProjects] = useState<IpdSkuSourceProjectOptionVO[]>([]);
  const [statusOptions, setStatusOptions] = useState<IpdSkuStatusOptionVO[]>([]);
  const [draftSelected, setDraftSelected] = useState<IpdSkuListItemVO[]>([]);
  const [confirming, setConfirming] = useState(false);

  const fetchList = useCallback(async (next?: {
    pageNum?: number;
    pageSize?: number;
    keyword?: string;
    sourceProjectWorkItemId?: number;
    status?: string[];
  }) => {
    if (!ipcProjectId) return;
    const pn = next?.pageNum ?? pageNum;
    const ps = next?.pageSize ?? pageSize;
    const kw = next?.keyword ?? keyword;
    const source = next?.sourceProjectWorkItemId ?? sourceProjectWorkItemId;
    const st = next?.status ?? status;
    setLoading(true);
    try {
      const res = await ipdSkuApi.getList({
        ipcProjectId,
        scheduleId,
        currentSpecId,
        pageNum: pn,
        pageSize: ps,
        keyword: kw || undefined,
        sourceProjectWorkItemId: source,
        status: st.length > 0 ? st.join(',') : undefined,
      });
      setList(res?.list ?? []);
      setTotal(res?.total ?? 0);
      setPageNum(res?.pageNum ?? pn);
      setPageSize(res?.pageSize ?? ps);
      if (res?.source_projects) setSourceProjects(res.source_projects);
      if (res?.status_options) setStatusOptions(res.status_options);
    } catch (e) {
      console.error('加载 IPD SKU 失败', e);
      message.error(e instanceof Error ? e.message : '加载 IPD SKU 失败');
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [ipcProjectId, scheduleId, currentSpecId, pageNum, pageSize, keyword, sourceProjectWorkItemId, status]);

  useEffect(() => {
    if (!open) return;
    setKeywordDraft('');
    setSourceDraft(undefined);
    setStatusDraft([]);
    setKeyword('');
    setSourceProjectWorkItemId(undefined);
    setStatus([]);
    setPageNum(1);
    setPageSize(PAGE_SIZE);
    setDraftSelected(selected);
    setConfirming(false);
    void fetchList({
      pageNum: 1,
      pageSize: PAGE_SIZE,
      keyword: '',
      sourceProjectWorkItemId: undefined,
      status: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ipcProjectId]);

  const commitFilters = useCallback((next: {
    keyword: string;
    sourceProjectWorkItemId: number | undefined;
    status: string[];
  }, skipIfUnchanged = false) => {
    if (
      skipIfUnchanged
      && next.keyword === keyword
      && next.sourceProjectWorkItemId === sourceProjectWorkItemId
      && JSON.stringify(next.status) === JSON.stringify(status)
    ) return;
    setKeyword(next.keyword);
    setKeywordDraft(next.keyword);
    setSourceDraft(next.sourceProjectWorkItemId);
    setSourceProjectWorkItemId(next.sourceProjectWorkItemId);
    setStatus(next.status);
    setStatusDraft(next.status);
    setPageNum(1);
    void fetchList({
      pageNum: 1,
      keyword: next.keyword,
      sourceProjectWorkItemId: next.sourceProjectWorkItemId,
      status: next.status,
    });
  }, [fetchList, keyword, sourceProjectWorkItemId, status]);

  const applyFilters = useCallback(() => {
    commitFilters({
      keyword: keywordDraft.trim(),
      sourceProjectWorkItemId: sourceDraft,
      status: statusDraft,
    }, true);
  }, [commitFilters, keywordDraft, sourceDraft, statusDraft]);

  const resetFilters = useCallback(() => {
    setKeywordDraft('');
    setSourceDraft(undefined);
    setStatusDraft([]);
    setKeyword('');
    setSourceProjectWorkItemId(undefined);
    setStatus([]);
    setPageNum(1);
    void fetchList({
      pageNum: 1,
      keyword: '',
      sourceProjectWorkItemId: undefined,
      status: [],
    });
  }, [fetchList]);

  const sourceLabel = useCallback((workItemId: number) => {
    const hit = sourceProjects.find(p => p.work_item_id === workItemId);
    if (!hit) return String(workItemId);
    return formatIpdSkuSource({
      source_project_code: hit.project_code,
      source_project_name: hit.name,
    });
  }, [sourceProjects]);

  const statusLabel = useCallback((value: string) => {
    const hit = statusOptions.find(s => s.value === value);
    return hit?.label || value;
  }, [statusOptions]);

  const appliedChips = [
    keyword.trim() ? { key: 'keyword', label: `搜索: ${keyword.trim()}` } : null,
    sourceProjectWorkItemId
      ? { key: 'source', label: `来源: ${sourceLabel(sourceProjectWorkItemId)}` }
      : null,
    ...status.map(value => ({ key: `status:${value}`, label: `状态: ${statusLabel(value)}` })),
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeChip = (key: string) => {
    let nextKeyword = keyword;
    let nextSource = sourceProjectWorkItemId;
    let nextStatus = status;
    if (key === 'keyword') nextKeyword = '';
    else if (key === 'source') nextSource = undefined;
    else if (key.startsWith('status:')) {
      nextStatus = status.filter(s => s !== key.slice('status:'.length));
    } else return;
    commitFilters({
      keyword: nextKeyword,
      sourceProjectWorkItemId: nextSource,
      status: nextStatus,
    });
  };

  const resolveOccupancy = (row: IpdSkuListItemVO): IpdSkuOccupancy => {
    const fromApi = (row.occupancy ?? '').toLowerCase();
    if (fromApi === 'other' || fromApi === 'current' || fromApi === 'available') {
      return fromApi;
    }
    if (getOccupancy) return getOccupancy(row);
    return 'available';
  };

  const columns: ColumnsType<IpdSkuListItemVO> = useMemo(() => [
    {
      title: '规格',
      dataIndex: 'spec',
      width: 140,
      ellipsis: true,
      render: (value: string, row) => (
        <span className="ipd-picker-code-name">
          {value?.trim() || '-'}
          {(row.occupancy ?? '').toLowerCase() === 'other' ? (
            <IpcTag variant="muted">已占用</IpcTag>
          ) : null}
        </span>
      ),
    },
    {
      title: '名称',
      dataIndex: 'name',
      width: 200,
      ellipsis: true,
      render: (value: string) => value?.trim() || '-',
    },
    {
      title: 'SKU 编码',
      dataIndex: 'sku_code',
      width: 140,
      ellipsis: true,
      render: (value: string) => value?.trim() || '-',
    },
    {
      title: '来源 IPD 项目',
      key: 'source',
      width: 260,
      ellipsis: true,
      render: (_, row) => formatIpdSkuSource(row),
    },
  ], []);

  const handleConfirm = async () => {
    if (readOnly) return;
    if (draftSelected.length === 0) {
      message.warning('请选择规格');
      return;
    }
    const blocked = draftSelected.filter(r => resolveOccupancy(r) === 'other');
    if (blocked.length > 0) {
      message.warning('所选规格中包含已被其他规格或其他项目引用的项');
      return;
    }
    setConfirming(true);
    try {
      await onOk(draftSelected);
    } catch {
      // 调用方提示
    } finally {
      setConfirming(false);
    }
  };

  const title = titlePrefix;

  return (
    <IpcModal
      title={title}
      open={open}
      onOk={() => { void handleConfirm(); }}
      onCancel={onCancel}
      okText="确认添加"
      cancelText={readOnly ? '关闭' : '取消'}
      width={980}
      style={{ maxWidth: '96vw' }}
      confirmLoading={confirming}
      className="ipd-sku-picker-modal"
      footer={
        readOnly ? (
          <div className="ipc-modal__footer-actions">
            <IpcButton variant="primary" onClick={onCancel}>
              关闭
            </IpcButton>
          </div>
        ) : undefined
      }
    >
      {currentRowLabel ? (
        <div className="ipd-sku-picker-context">{currentRowLabel}</div>
      ) : null}
      {draftSelected.length > 0 && (
        <div className="ipd-picker-selected">
          <span className="ipd-picker-selected__label">
            {readOnly ? `当前行关联: ${draftSelected.length} 项` : `当前行已选: ${draftSelected.length} 项`}
          </span>
          <div className="ipd-picker-selected__tags">
            {draftSelected.map(row => {
              const label = formatIpdSkuLabel(row);
              return (
                <IpcTag
                  key={row.id}
                  variant="primary"
                  closable={!readOnly}
                  title={label}
                  onClose={readOnly ? undefined : () => setDraftSelected(prev => prev.filter(r => r.id !== row.id))}
                >
                  {label}
                </IpcTag>
              );
            })}
          </div>
          {!readOnly && (
            <button
              type="button"
              className="ipd-picker-selected__clear"
              onClick={() => setDraftSelected([])}
            >
              清空
            </button>
          )}
        </div>
      )}
      {!readOnly && (
        <div className="ipd-sku-picker-hint">
          所选规格将合并到当前行，共用同一套里程碑。已被其他规格或其他项目的草稿/已发布版本引用的 SKU 不可选择。
        </div>
      )}
      {readOnly && (
        <div className="ipd-sku-picker-hint">
          当前为查看模式，不可修改关联规格。
        </div>
      )}
      <IpcFilterToolbar
        onReset={resetFilters}
        onSubmit={applyFilters}
        chipItems={appliedChips}
        onRemoveChip={removeChip}
        onClearChips={resetFilters}
      >
        <Input
          placeholder="规格 / 名称 / SKU 搜索..."
          value={keywordDraft}
          onChange={e => setKeywordDraft(e.target.value)}
          onPressEnter={applyFilters}
          allowClear
          className="ipc-filter-bar__field"
        />
        <Select
          placeholder="全部来源项目"
          value={sourceDraft}
          onChange={v => {
            commitFilters({
              keyword: keywordDraft.trim(),
              sourceProjectWorkItemId: v,
              status: statusDraft,
            }, true);
          }}
          options={sourceProjects.map(p => ({
            value: p.work_item_id,
            label: formatIpdSkuSource({
              source_project_code: p.project_code,
              source_project_name: p.name,
            }),
          }))}
          allowClear
          showSearch
          optionFilterProp="label"
          getPopupContainer={() => document.body}
          className="ipc-filter-bar__field"
        />
        <Select
          mode="multiple"
          placeholder="全部状态"
          value={statusDraft}
          onChange={v => {
            commitFilters({
              keyword: keywordDraft.trim(),
              sourceProjectWorkItemId: sourceDraft,
              status: v ?? [],
            }, true);
          }}
          options={statusOptions.map(s => ({ value: s.value, label: s.label || s.value }))}
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          className="ipc-filter-bar__field"
        />
      </IpcFilterToolbar>
      <IpcTable<IpdSkuListItemVO>
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        compact
        frameless
        showHeaderIcon={false}
        selectedCount={draftSelected.length}
        scroll={{ y: 360 }}
        rowSelection={{
          selectedRowKeys: draftSelected.map(r => r.id),
          preserveSelectedRowKeys: true,
          getCheckboxProps: row => ({
            disabled: readOnly || resolveOccupancy(row) === 'other',
          }),
          hideSelectAll: readOnly,
          onChange: (_keys: Key[], rows) => {
            if (readOnly) return;
            const pageIds = new Set(list.map(r => r.id));
            const kept = draftSelected.filter(r => !pageIds.has(r.id));
            const currentPageSelected = rows.filter((r): r is IpdSkuListItemVO => r != null);
            const merged = [...kept, ...currentPageSelected];
            if (merged.some(r => resolveOccupancy(r) === 'other')) {
              message.warning('不可选择已被其他规格或其他项目引用的规格');
              return;
            }
            setDraftSelected(merged);
          },
        }}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPageNum(p);
            setPageSize(ps);
            void fetchList({ pageNum: p, pageSize: ps });
          },
        }}
        emptyDescription="暂无可用规格"
      />
    </IpcModal>
  );
};
